(function (global, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else global.AskSeyuneTurnstile = factory();
}(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  var TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

  function makeError(code, clientErrorCode) {
    var error = new Error(clientErrorCode ? code + ':' + clientErrorCode : code);
    error.code = code;
    if (clientErrorCode) error.clientErrorCode = clientErrorCode;
    return error;
  }

  function normalizeClientErrorCode(value) {
    var code = typeof value === 'number' ? String(value) : typeof value === 'string' ? value.trim() : '';
    return /^\d{3,6}$/.test(code) ? code : 'unknown';
  }

  function reportClientError(value) {
    var clientErrorCode = normalizeClientErrorCode(value);
    try {
      console.warn('[Ask Seyune] Turnstile client error: ' + clientErrorCode);
    } catch (error) {
      // Diagnostics must never affect the verification attempt.
    }
    return clientErrorCode;
  }

  function createTurnstileGate(config) {
    config = config || {};
    var siteKey = String(config.siteKey || '');
    var host = config.host;
    var configuredApi = config.api || null;
    var widget = null;
    var apiPromise = null;
    var attemptGeneration = 0;
    var activeAttempt = null;
    var inFlight = false;
    var deadlineTimer = null;
    var cachedToken = '';
    var cachedAt = 0;
    var prewarmPromise = null;
    var leaseGeneration = 0;
    var TOKEN_MAX_AGE_MS = 4 * 60 * 1000;

    function currentApi() {
      return configuredApi || (typeof globalThis !== 'undefined' ? globalThis.turnstile : null);
    }

    function stopDeadline() {
      if (deadlineTimer) {
        clearTimeout(deadlineTimer);
        deadlineTimer = null;
      }
    }

    function clearFailedScript(script) {
      if (script && script.parentNode && typeof script.parentNode.removeChild === 'function') {
        script.parentNode.removeChild(script);
      }
    }

    function isValidWidgetId(value) {
      return (typeof value === 'number' && Number.isFinite(value) && value >= 0) ||
        (typeof value === 'string' && value.length > 0 && value.length <= 128);
    }

    function cleanupWidget() {
      var client = currentApi();
      var currentWidget = widget;
      widget = null;
      if (!client || !isValidWidgetId(currentWidget) || typeof client.remove !== 'function') return;
      try {
        client.remove(currentWidget);
      } catch (error) {
        // Cleanup is best effort; local state is already cleared.
      }
    }

    function isCurrent(attempt) {
      return activeAttempt === attempt &&
        attempt.generation === attemptGeneration &&
        !attempt.invalidated;
    }

    function invalidate(attempt, error) {
      if (!isCurrent(attempt)) return;
      attemptGeneration += 1;
      attempt.invalidated = true;
      activeAttempt = null;
      inFlight = false;
      stopDeadline();
      if (!attempt.settled) {
        attempt.settled = true;
        attempt.reject(error || makeError('turnstile_reset'));
      }
      cleanupWidget();
    }

    function resolveAttempt(generation, value) {
      var attempt = activeAttempt;
      if (!attempt || attempt.generation !== generation || attempt.invalidated || attempt.settled) return;
      var next = String(value || '');
      if (!next) return;
      stopDeadline();
      attempt.token = next;
      attempt.settled = true;
      attempt.resolve(next);
    }

    function rejectAttempt(generation, code, clientErrorCode) {
      var attempt = activeAttempt;
      if (!attempt || attempt.generation !== generation || attempt.invalidated || attempt.settled) return;
      invalidate(attempt, makeError(code, clientErrorCode));
    }

    function abortable(promise, signal) {
      if (!signal) return promise;
      if (signal.aborted) return Promise.reject(makeError('cancelled'));
      return Promise.race([
        promise,
        new Promise(function (resolve, reject) {
          signal.addEventListener('abort', function () {
            reject(makeError('cancelled'));
          }, { once: true });
        })
      ]);
    }

    function loadClient(signal) {
      var ready = currentApi();
      if (ready) return Promise.resolve(ready);
      if (configuredApi || typeof document === 'undefined') {
        return Promise.reject(makeError('turnstile_unavailable'));
      }
      if (!apiPromise) {
        apiPromise = new Promise(function (resolve, reject) {
          var script = document.querySelector('script[data-ask-seyune-turnstile]');
          var settled = false;
          var timer = null;

          function fail(error) {
            if (settled) return;
            settled = true;
            if (timer) clearTimeout(timer);
            apiPromise = null;
            clearFailedScript(script);
            reject(error || makeError('turnstile_unavailable'));
          }

          function finish(error) {
            if (settled) return;
            if (error) {
              fail(error);
              return;
            }
            var client = currentApi();
            if (!client) {
              fail(makeError('turnstile_unavailable'));
              return;
            }
            settled = true;
            if (timer) clearTimeout(timer);
            resolve(client);
          }

          timer = setTimeout(function () {
            fail(makeError('turnstile_unavailable'));
          }, 7000);

          if (!script) {
            script = document.createElement('script');
            script.src = TURNSTILE_SCRIPT_URL;
            script.async = true;
            script.defer = true;
            script.setAttribute('data-ask-seyune-turnstile', 'true');
            script.addEventListener('load', function () { finish(); }, { once: true });
            script.addEventListener('error', function () { finish(makeError('turnstile_unavailable')); }, { once: true });
            (document.head || document.documentElement).appendChild(script);
          } else {
            script.addEventListener('load', function () { finish(); }, { once: true });
            script.addEventListener('error', function () { finish(makeError('turnstile_unavailable')); }, { once: true });
            if (currentApi()) finish();
          }
        });
      }
      return abortable(apiPromise, signal);
    }

    function renderAttempt(attempt, client) {
      if (!siteKey || !host || !client || typeof client.render !== 'function') return false;
      if (widget !== null) cleanupWidget();
      var generation = attempt.generation;
      try {
        widget = client.render(host, {
          sitekey: siteKey,
          action: 'ask_seyune',
          execution: 'execute',
          size: 'flexible',
          appearance: 'interaction-only',
          callback: function (value) {
            resolveAttempt(generation, value);
          },
          'expired-callback': function () {
            rejectAttempt(generation, 'turnstile_expired');
          },
          'timeout-callback': function () {
            rejectAttempt(generation, 'turnstile_timeout');
          },
          'error-callback': function (clientErrorCode) {
            var currentAttempt = activeAttempt;
            if (!currentAttempt || currentAttempt.generation !== generation || currentAttempt.invalidated || currentAttempt.settled) return true;
            var safeClientErrorCode = reportClientError(clientErrorCode);
            rejectAttempt(generation, 'turnstile_error', safeClientErrorCode);
            return true;
          }
        });
      } catch (error) {
        invalidate(attempt, makeError('turnstile_error'));
        return false;
      }
      return widget !== null && widget !== undefined;
    }

    function getToken(signal) {
      if (cachedToken && (Date.now() - cachedAt) < TOKEN_MAX_AGE_MS) {
        var readyToken = cachedToken;
        cachedToken = '';
        cachedAt = 0;
        return Promise.resolve(readyToken);
      }
      if (cachedToken) {
        cachedToken = '';
        cachedAt = 0;
        cleanupWidget();
      }
      if (inFlight) return Promise.reject(makeError('turnstile_busy'));

      var attempt = {
        generation: ++attemptGeneration,
        invalidated: false,
        settled: false,
        token: '',
        resolve: null,
        reject: null
      };
      activeAttempt = attempt;
      inFlight = true;

      var promise = new Promise(function (resolve, reject) {
        attempt.resolve = resolve;
        attempt.reject = reject;
      });

      if (signal && signal.aborted) {
        invalidate(attempt, makeError('cancelled'));
        return promise;
      }

      loadClient(signal).then(function (client) {
        if (!isCurrent(attempt)) return;
        if (signal && signal.aborted) {
          invalidate(attempt, makeError('cancelled'));
          return;
        }
        if (!renderAttempt(attempt, client)) {
          invalidate(attempt, makeError('turnstile_unavailable'));
          return;
        }
        deadlineTimer = setTimeout(function () {
          rejectAttempt(attempt.generation, 'turnstile_unavailable');
        }, 5500);
        try {
          var execution = client.execute(widget);
          if (execution && typeof execution.catch === 'function') {
            execution.catch(function () {
              if (isCurrent(attempt)) invalidate(attempt, makeError('turnstile_error'));
            });
          }
        } catch (error) {
          invalidate(attempt, makeError('turnstile_error'));
        }
      }, function (error) {
        if (isCurrent(attempt)) invalidate(attempt, error);
      });

      if (signal) {
        signal.addEventListener('abort', function () {
          if (isCurrent(attempt)) invalidate(attempt, makeError('cancelled'));
        }, { once: true });
      }
      return promise;
    }

    function prewarm(signal) {
      if (cachedToken && (Date.now() - cachedAt) < TOKEN_MAX_AGE_MS) return Promise.resolve(cachedToken);
      if (prewarmPromise) return prewarmPromise;
      var generation = ++leaseGeneration;
      var lease;
      lease = getToken(signal).then(function (token) {
        if (generation !== leaseGeneration) return '';
        cachedToken = token;
        cachedAt = Date.now();
        return token;
      }).catch(function () {
        return '';
      }).then(function (token) {
        if (generation !== leaseGeneration) return '';
        if (activeAttempt && activeAttempt.settled) {
          activeAttempt = null;
          inFlight = false;
          cleanupWidget();
        }
        return token;
      }).finally(function () {
        if (prewarmPromise === lease) prewarmPromise = null;
      });
      prewarmPromise = lease;
      return lease;
    }

    function waitForPrewarm() {
      return prewarmPromise || Promise.resolve(cachedToken);
    }

    function reset() {
      leaseGeneration += 1;
      cachedToken = '';
      cachedAt = 0;
      if (activeAttempt) {
        invalidate(activeAttempt, makeError('turnstile_reset'));
        return;
      }
      attemptGeneration += 1;
      inFlight = false;
      stopDeadline();
      cleanupWidget();
    }

    return {
      getToken: getToken,
      prewarm: prewarm,
      waitForPrewarm: waitForPrewarm,
      reset: reset,
      cancel: reset,
      destroy: reset,
      render: function () { return widget !== null; }
    };
  }

  return { createTurnstileGate: createTurnstileGate };
}));